/**
 * #591: hardened localStorage accessors, shared app-wide.
 *
 * GMA-1275 established (Sentry COMPASSCARE-FRONTEND-1G) that Mobile Safari
 * can return a null `localStorage` accessor during page teardown /
 * cross-context storage events, and Safari privacy modes (Block All Cookies,
 * Lockdown) make any access THROW. useJwtAsState got private safe accessors
 * then; AuthContext never did — its raw `localStorage.clear()` ran on
 * /register mount via logout(), so an unlucky Safari state collapsed the React
 * tree to a blank page for a caregiver mid-registration. #785 removed that
 * mount-time call, but logout() still runs on a successful registration from
 * the same page, so the exposure moved rather than went away.
 *
 * One shared module now owns the pattern (house rule: no second copy of
 * solved logic). Every accessor short-circuits to a safe default and emits a
 * single `auth.localStorage.unavailable` breadcrumb per page load.
 */
import { addBreadcrumb } from "../config/sentry";

let _storageUnavailableBreadcrumbSent = false;

export const getStorage = () => {
  try {
    if (typeof window === "undefined") {
      return null;
    }
    const storage = window.localStorage;
    return storage || null;
  } catch (_err) {
    return null;
  }
};

const noteStorageUnavailable = (op) => {
  if (_storageUnavailableBreadcrumbSent) {
    return;
  }
  _storageUnavailableBreadcrumbSent = true;
  try {
    addBreadcrumb("auth.localStorage.unavailable", {
      // The category IS the searchable field — the fleet's dotted
      // <surface>.<area>.<action> convention, and what the confirming
      // filter names. (The first argument is only the message.)
      category: "auth.localStorage.unavailable",
      level: "warning",
      op,
    });
  } catch (_err) {
    // Breadcrumb itself must never throw out of storage access.
  }
};

export const safeGet = (key) => {
  const storage = getStorage();
  if (!storage) {
    noteStorageUnavailable(`get:${key}`);
    return null;
  }
  try {
    return storage.getItem(key);
  } catch (_err) {
    noteStorageUnavailable(`get:${key}`);
    return null;
  }
};

export const safeSet = (key, value) => {
  const storage = getStorage();
  if (!storage) {
    noteStorageUnavailable(`set:${key}`);
    return false;
  }
  try {
    storage.setItem(key, value);
    return true;
  } catch (_err) {
    // Quota errors land here too; a failed write is reported, not thrown.
    noteStorageUnavailable(`set:${key}`);
    return false;
  }
};

export const safeClear = () => {
  const storage = getStorage();
  if (!storage) {
    noteStorageUnavailable("clear");
    return;
  }
  try {
    storage.clear();
  } catch (_err) {
    noteStorageUnavailable("clear");
  }
};

export const __test__ = {
  resetStorageUnavailableBreadcrumbDedupe: () => {
    _storageUnavailableBreadcrumbSent = false;
  },
};
